import React, { Component } from 'react';
import './MortalityAlert.less';
import Header2 from '../Header2';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from '../../lib/withRouter';
import { getViewPatientData, postResource } from '../../lib';

const API = import.meta.env.VITE_BASE_URL || '';
const PRIORITY_RANK = { critical: 0, high: 1, normal: 2 };
const PAGE_SIZE = 50;
const priorityOf = (a) => a.triage?.priority || a.priority;
const patientNameOf = (a) => a.patientName || a.patient?.displayName || 'Unknown patient';
const locationOf = (a) => a.location || a.encounter?.location || '';
const messageCountOf = (a) => a.messageCount ?? a.messages?.length ?? 0;
const deathTime = (a) => a.death?.datetime || a.deathDatetime || a.encounter?.dischargeDatetime || a.createdAt;

function chime() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 660].forEach((f, i) => {
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
            g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.25);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.25 + 0.22);
            o.start(ctx.currentTime + i * 0.25); o.stop(ctx.currentTime + i * 0.25 + 0.25);
        });
    } catch { /* audio blocked until user interacts */ }
}

function ago(iso) {
    if (!iso) return '—';
    const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h}h ${m % 60}m ago` : `${Math.floor(h / 24)}d ago`;
}
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export class MortalityAlerts extends Component {
    static propTypes = {
        query: PropTypes.object,
        settings: PropTypes.object,
        location: PropTypes.object,
        urlParams: PropTypes.object,
    };
    constructor(props) {
        super(props);
        this.state = {
            alerts: [],
            kpis: { new: 0, criticalOpen: 0, open: 0 },
            hasMore: false,
            loading: false,
            connected: false,
            sessionUser: null,
            statusFilter: 'open',
            typeFilter: 'all',
            selectedId: null,
            detail: null,
            user: (() => { try { return localStorage.getItem('alertUser') || ''; } catch { return ''; } })(),
        };
    }
    known = new Set();
    initialised = false;
    mounted = false;
    tickTimer = null;
    inflight = null;
    alertRequestId = 0;

    componentDidMount() {
        this.mounted = true;
        this.refresh();
        this.tickTimer = setInterval(() => this.forceUpdate(), 30000);
    }

    componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.tickTimer);
    }

    refresh = (reset = false, force = false) => {
        if (force || !this.inflight) {
            const p = Promise.all([this.loadKpis(), this.loadAlerts({ reset })])
                .finally(() => { if (this.inflight === p) this.inflight = null; });
            this.inflight = p;
        }
        return this.inflight;
    };

    loadKpis = () => getViewPatientData({
        url: `${API}/mortality/kpis`,
        server: this.props.settings.server,
    })
        .then((kpis) => this.setState({ kpis }))
        .catch((error) => console.error('Mortality KPIs failed:', error));

    loadAlerts = async ({ append = false, reset = false } = {}) => {
        const { statusFilter, typeFilter, alerts } = this.state;
        const limit = append || reset ? PAGE_SIZE : Math.max(PAGE_SIZE, alerts.length);
        const offset = append ? alerts.length : 0;
        const qs = new URLSearchParams({ status: statusFilter, encounterType: typeFilter, limit, offset });
        const requestId = ++this.alertRequestId;
        if (append || reset) this.setState({ loading: true });
        try {
            const d = await getViewPatientData({
                url: `${API}/mortality/alerts?${qs}`,
                server: this.props.settings.server,
            });
            if (!this.mounted || requestId !== this.alertRequestId) return;
            d.alerts.forEach((a) => {
                if (this.known.has(a.id)) return;
                this.known.add(a.id);
                if (this.initialised && !append && !reset && a.status === 'new') this.onNewAlert(a);
            });
            this.initialised = true;
            this.setState((s) => ({
                alerts: append ? [...s.alerts, ...d.alerts.filter((a) => !s.alerts.some((x) => x.id === a.id))] : d.alerts,
                hasMore: d.hasMore,
                connected: true,
                loading: false,
            }));
        } catch (error) {
            if (!this.mounted || requestId !== this.alertRequestId) return;
            console.error('Mortality alerts failed:', error);
            this.setState({ connected: false, loading: false });
        }
    };

    fetchDetail = (id) => getViewPatientData({
        url: `${API}/mortality/alerts/${id}`,
        server: this.props.settings.server,
    })
        .then((detail) => this.setState((s) => (s.selectedId === id ? { detail } : null)))
        .catch((error) => console.error('Mortality alert detail failed:', error));

    selectAlert = (id) => {
        this.setState({ selectedId: id, detail: null });
        this.fetchDetail(id);
    };

    changeFilter = (key, v) => {
        this.alertRequestId += 1;
        this.setState({ [key]: v, alerts: [], hasMore: false }, () => this.refresh(true, true));
    };

    onNewAlert = (a) => {
        chime();
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(`${a.encounterType} death alert`, { body: locationOf(a) });
        }
        if (!this.state.selectedId) this.selectAlert(a.id);
    };

    setStatus = async (id, status) => {
        const { sessionUser, user } = this.state;
        if (!sessionUser && !user.trim()) { alert('Enter your name in the header so the acknowledgement is attributed.'); return; }
        try {
            await postResource({
                url: `${API}/mortality/alerts/${id}/status`,
                server: this.props.settings.server,
                data: { status, user },
            });
            await Promise.all([this.refresh(false, true), this.fetchDetail(id)]);
        } catch (error) {
            console.error('Updating mortality alert status failed:', error);
        }
    };

    render() {
        const { alerts, kpis: counts, hasMore, loading, connected, sessionUser, statusFilter, typeFilter, selectedId, user, detail } = this.state;
        const agentMode = detail?.triage?.source === 'llm' ? 'llm' : '';

        const visible = [...alerts].sort((a, b) => (a.status === 'new' ? 0 : 1) - (b.status === 'new' ? 0 : 1)
            || (PRIORITY_RANK[priorityOf(a)] ?? -1) - (PRIORITY_RANK[priorityOf(b)] ?? -1)
            || Date.parse(b.createdAt) - Date.parse(a.createdAt));

        const listItem = alerts.find((a) => a.id === selectedId);
        const selected = detail && detail.id === selectedId ? { ...detail, ...listItem } : null;

        return (
            <div className="page">
                <div className="app-header">
                    <Header2
                        query={this.props.query}
                        settings={this.props.settings}
                        location={this.props.location}
                        urlParams={this.props.urlParams}
                    />
                </div>

                <div className="">
                    <div className="ma-root">
                        <header className="ma-topbar">
                            <div className="ma-brand">
                                <span className="ma-brand-mark" aria-hidden="true">✚</span>
                                <div>
                                    <h1>Mortality Alerts</h1>
                                    <p>ADT and C-CDA death notifications · Emergency &amp; Inpatient</p>
                                </div>
                            </div>
                            <div className="ma-stats">
                                <Stat label="New" value={counts.new} tone={counts.new ? 'alert' : ''} />
                                <Stat label="Critical open" value={counts.criticalOpen} tone={counts.criticalOpen ? 'critical' : ''} />
                                <Stat label="Open" value={counts.open} />
                            </div>
                            <div className="ma-top-right">
                                <span className={`ma-live ${connected ? 'ma-live--on' : 'ma-live--off'}`}>
                                    <span className="ma-live-dot" />{connected ? 'Live' : 'Reconnecting…'}
                                </span>
                                <span className="ma-chip" title="How alerts are triaged">Triage: {agentMode === 'llm' ? 'AI agent' : 'Rules'}</span>
                                {sessionUser
                                    ? <span className="ma-chip" title="Signed in through SSO">{sessionUser.name}</span>
                                    : <input className="form-control input-sm ma-user" placeholder="Your name" value={user} onChange={(e) => this.setState({ user: e.target.value })} aria-label="Your name" />}
                                {/* {'Notification' in window && Notification.permission === 'default' && (
                                    <button type="button" className="btn btn-default btn-sm" onClick={() => Notification.requestPermission()}>Enable desktop alerts</button>
                                )} */}
                            </div>
                        </header>

                        <div className="ma-filters">
                            <Segmented value={statusFilter} onChange={(v) => this.changeFilter('statusFilter', v)}
                                options={[['open', 'Open'], ['new', 'New'], ['acknowledged', 'Acknowledged'], ['resolved', 'Resolved'], ['all', 'All']]} />
                            <Segmented value={typeFilter} onChange={(v) => this.changeFilter('typeFilter', v)}
                                options={[['all', 'All encounters'], ['Emergency', 'Emergency'], ['Inpatient', 'Inpatient']]} />
                        </div>

                        <main className="ma-main">
                            <div className="row">
                                <section className="col-md-5 col-lg-4" aria-label="Alerts">
                                    <div className="ma-list">
                                        {visible.length === 0 && <div className="ma-empty">No alerts match these filters.</div>}
                                        {visible.map((a) => (
                                            <button key={a.id} type="button"
                                                className={`ma-card ma-card--${priorityOf(a) || 'pending'} ${a.id === selectedId ? 'ma-sel' : ''} ma-${a.status}`}
                                                onClick={() => this.selectAlert(a.id)}>
                                                <div className="ma-card-top">
                                                    <PriorityPill p={priorityOf(a)} />
                                                    <span className={`label ma-enc ma-enc--${a.encounterType || 'unknown'}`}>{a.encounterType || '—'}</span>
                                                    <Sources a={a} />
                                                    {a.status === 'new' && <span className="ma-newtag">NEW</span>}
                                                    <span className="ma-when">{ago(deathTime(a))}</span>
                                                </div>
                                                <div className="ma-card-name">{patientNameOf(a)}</div>
                                                <div className="ma-card-head">{a.triage?.headline || a.headline || 'Agent is triaging…'}</div>
                                                <div className="ma-card-meta">
                                                    <span>{locationOf(a) || 'Unknown unit'}</span>
                                                    <span>MRN {a.facts?.patient?.mrn || a.mrn || '—'}</span>
                                                    {messageCountOf(a) > 1 && <span>{messageCountOf(a)} msgs</span>}
                                                </div>
                                            </button>
                                        ))}
                                        {hasMore && (
                                            <button type="button" className="btn btn-default btn-sm" disabled={loading} onClick={() => this.loadAlerts({ append: true })}>
                                                {loading ? 'Loading…' : 'Load more'}
                                            </button>
                                        )}
                                    </div>
                                </section>

                                <section className="col-md-7 col-lg-8" aria-label="Alert detail">
                                    <div className="ma-detail">
                                        {selected ? <Detail key={selected.id} a={selected} onStatus={this.setStatus} server={this.props.settings.server} /> : <div className="ma-empty">{selectedId ? 'Loading…' : 'Select an alert to see details.'}</div>}
                                    </div>
                                </section>
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        );
    }
}

function Stat({ label, value, tone }) {
    return <div className={`ma-stat ${tone ? `ma-stat--${tone}` : ''}`}><b>{value}</b><span>{label}</span></div>;
}

function Segmented({ value, onChange, options }) {
    return (
        <div className="ma-seg" role="tablist">
            {options.map(([v, l]) => (
                <button key={v} type="button" role="tab" aria-selected={value === v} className={value === v ? 'ma-on' : ''} onClick={() => onChange(v)}>{l}</button>
            ))}
        </div>
    );
}

function Sources({ a }) {
    const list = a.sourceSummary?.sources || a.sources;
    if (!list?.length) return null;
    return <>{list.map((s) => <span key={s} className={`label ma-src ${s === 'C-CDA' ? 'ma-src--ccda' : ''}`}>{s}</span>)}</>;
}

function PriorityPill({ p }) {
    return <span className={`label ma-pill ma-pill--${p || 'pending'}`}>{p || 'triaging'}</span>;
}

class Detail extends Component {
    state = { raw: null, showTrace: false };

    loadRaw = async () => {
        const { a } = this.props;
        const parts = await Promise.all((a.messages || []).map(async (m) => {
            let text = await getViewPatientData({
                url: `${API}/mortality/alerts/${a.id}/messages/${m.id}/raw`,
                server: this.props.server,
                dataType: 'text',
            });
            if (typeof text !== 'string') text = text?.raw ?? JSON.stringify(text, null, 2);
            try { text = JSON.parse(text).raw ?? text; } catch { /* plain text */ }
            return `# ${m.sourceType || 'ADT'} · ${m.eventType} · ${m.controlId} · received ${fmt(m.receivedAt)}\n${text.split('\r').join('\n')}`;
        }));
        this.setState({ raw: parts.join('\n\n') });
    };

    render() {
        const { a, onStatus } = this.props;
        const { raw, showTrace } = this.state;
        const patient = a.patient || {};
        const encounter = a.encounter || {};
        const death = a.death || {};
        const t = a.triage;
        const messages = a.messages || [];
        const audit = a.audit || [];
        const evidence = death.evidence || [];
        const diagnoses = a.diagnoses || [];
        const encounterType = a.encounterType || '—';
        const priority = priorityOf(a);

        return (
            <div>
                <div className={`ma-banner ma-banner--${priority || 'pending'}`}>
                    <div>
                        <div className="ma-banner-top">
                            <PriorityPill p={priority} />
                            <span className={`label ma-enc ma-enc--${encounterType}`}>{encounterType}</span>
                            <Sources a={a} />
                            <span className="ma-status">{a.status}</span>
                        </div>
                        <h2>{patientNameOf(a)}</h2>
                        <p className="ma-sub">MRN {patient.mrn || a.mrn || '—'} · Visit {patient.visitNumber || '—'} · {patient.sex || a.sex || '—'} · DOB {patient.dob ? patient.dob.slice(0, 10) : '—'}</p>
                    </div>
                    <div className="ma-actions">
                        {a.status === 'new' && <button type="button" className="btn btn-primary" onClick={() => onStatus(a.id, 'acknowledged')}>Acknowledge</button>}
                        {a.status !== 'resolved' && <button type="button" className="btn btn-default" onClick={() => onStatus(a.id, 'resolved')}>Resolve</button>}
                        {a.status === 'resolved' && <button type="button" className="btn btn-default" onClick={() => onStatus(a.id, 'acknowledged')}>Reopen</button>}
                    </div>
                </div>

                <div className="ma-grid2">
                    <Panel title="Agent triage">
                        {t ? (
                            <>
                                <p className="ma-headline">{t.headline || a.headline}</p>
                                <p>{t.summary}</p>
                                {t.flags?.length > 0 && <div className="ma-flags">{t.flags.map((x) => <span key={x} className="ma-flag">⚑ {x}</span>)}</div>}
                                <h4>Notify</h4>
                                <ul className="ma-notify">{(t.notify || []).map((n) => <li key={n}>{n}</li>)}</ul>
                                <p className="ma-muted ma-small">Source: {t.source} · {fmt(t.triagedAt)}</p>
                                {t.playbook && <p className="ma-muted ma-small">{t.playbook}</p>}
                                {t.rationale && <p className="ma-muted ma-small">{t.rationale}</p>}
                                {t.trace?.length > 0 && (
                                    <>
                                        <button type="button" className="ma-link" onClick={() => this.setState((s) => ({ showTrace: !s.showTrace }))}>{showTrace ? 'Hide' : 'Show'} agent steps ({t.trace.length})</button>
                                        {showTrace && <pre className="ma-trace">{JSON.stringify(t.trace, null, 2)}</pre>}
                                    </>
                                )}
                            </>
                        ) : <p className="ma-muted">The agent is reviewing this alert…</p>}
                    </Panel>

                    <Panel title="Death">
                        <KV k="Date / time" v={fmt(deathTime(a))} />
                        <KV k="Evidence" v={<ul>{evidence.map((e) => <li key={e.id}>{e.text}</li>)}</ul>} />
                        <KV k="Place of death" v={death.location || '—'} />
                        <KV k="Autopsy" v={death.autopsy || '—'} />
                        <KV k="Coroner" v={death.coroner || '—'} />
                        <KV k="Certified by" v={death.certifiedBy || '—'} />
                        {diagnoses.length > 0 && <KV k="Diagnosis" v={<ul>{diagnoses.map((d) => <li key={d.id}>{d.code} {d.text}</li>)}</ul>} />}
                    </Panel>

                    <Panel title="Encounter">
                        <KV k="Location" v={encounter.location || '—'} />
                        <KV k="Facility" v={encounter.facility || '—'} />
                        <KV k="Patient class" v={`${encounter.patientClass || '—'} (${encounterType})`} />
                        <KV k="Admission type" v={encounter.admissionType || '—'} />
                        <KV k="Attending" v={encounter.attendingName || '—'} />
                        <KV k="Admitted" v={fmt(encounter.admitDatetime)} />
                        <KV k="Discharged" v={fmt(encounter.dischargeDatetime)} />
                        <KV k="Disposition" v={encounter.dischargeDisposition || '—'} />
                    </Panel>

                    <Panel title="Messages & audit">
                        <ul className="ma-timeline">
                            {messages.map((m) => <li key={m.id}><b>{m.eventType}</b> {m.controlId} <span className="ma-muted">· {fmt(m.receivedAt)}</span></li>)}
                            {audit.map((x) => <li key={x.id} className="ma-audit"><b>{x.action}{x.note ? ` ${x.note}` : ''}</b> <span className="ma-muted">by {x.actor} · {fmt(x.eventAt)}</span></li>)}
                        </ul>
                        {messages.length > 0 && (raw ? <pre className="ma-raw">{raw}</pre> : <button type="button" className="ma-link" onClick={this.loadRaw}>View source messages (HL7 / C-CDA)</button>)}
                    </Panel>
                </div>
            </div>
        );
    }
}

function Panel({ title, children }) {
    return <div className="ma-panel"><h3>{title}</h3>{children}</div>;
}
function KV({ k, v }) {
    return <div className="ma-kv"><span>{k}</span><div>{v}</div></div>;
}

export default connect((state) => state)(withRouter(MortalityAlerts));